import {
  type JobCategoryTopGroup,
  MyNaviFilterJobCategoryAccordion,
} from '@/components/Campaign/mynavi/MyNaviModels/Filter/MyNaviFilterJobCategoryAccordion'
import { MyNaviFilterLayout } from '@/components/Campaign/mynavi/MyNaviModels/Filter/MyNaviFilterLayout'
import { useApi } from '@/components/Functional/hooks-core/useApi'
import type { MynaviJobCategoryNode } from '@/schemas/v2/mynavi'
import { Loader } from '@mantine/core'
import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'

type Props = {
  initialJobCategoryIds: number[]
  onSelect: (jobCategoryIds: number[]) => void
  onClear: () => void
  onClose: () => void
  jobCategories?: MynaviJobCategoryNode[]
}

export function FilterJobCategory({
  initialJobCategoryIds,
  onSelect,
  onClear,
  onClose,
  jobCategories: jobCategoriesProp,
}: Props) {
  const api = useApi()

  const [localSelected, setLocalSelected] = useState<number[]>(initialJobCategoryIds)

  const { data: jobCategoriesData, isLoading } = useQuery({
    queryKey: ['mynavi-job-categories'],
    queryFn: () => api.guest.fetchMynaviJobCategories(false),
    staleTime: 5 * 60 * 1000,
    enabled: jobCategoriesProp === undefined,
  })

  const { data: countData } = useQuery({
    queryKey: ['mynavi-filter-count', 'job-category', localSelected],
    queryFn: () => api.guest.fetchMynaviCollegesFilterCount({ jobCategoryIds: localSelected }),
    enabled: jobCategoriesProp === undefined,
    staleTime: 30 * 1000,
  })

  const resolvedJobCategories: MynaviJobCategoryNode[] = jobCategoriesProp ?? jobCategoriesData?.jobCategories ?? []

  const handleToggleItem = (id: number) => {
    setLocalSelected((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]))
  }

  const handleToggleGroupAll = (ids: number[]) => {
    const allSelected = ids.every((id) => localSelected.includes(id))
    if (allSelected) {
      setLocalSelected((prev) => prev.filter((id) => !ids.includes(id)))
    } else {
      setLocalSelected((prev) => Array.from(new Set([...prev, ...ids])))
    }
  }

  const groups: JobCategoryTopGroup[] = useMemo(() => {
    return resolvedJobCategories.map((topCategory) => ({
      id: topCategory.id,
      name: topCategory.name,
      defaultExpanded: false,
      middleGroups: (topCategory.children ?? []).map((middleCategory) => {
        const leafIds = (middleCategory.children ?? []).map((leaf) => leaf.id)
        return {
          id: middleCategory.id,
          name: middleCategory.name,
          defaultExpanded: false,
          allChecked: leafIds.length > 0 && leafIds.every((id) => localSelected.includes(id)),
          onToggleAll: () => handleToggleGroupAll(leafIds),
          items: (middleCategory.children ?? []).map((leaf) => ({
            id: leaf.id,
            label: leaf.name,
            checked: localSelected.includes(leaf.id),
            onToggle: () => handleToggleItem(leaf.id),
          })),
        }
      }),
    }))
  }, [resolvedJobCategories, localSelected])

  const handleSelect = () => {
    onSelect(localSelected)
  }

  const handleClear = () => {
    setLocalSelected([])
    onClear()
  }

  return (
    <MyNaviFilterLayout
      title="目指せる職種を選択する"
      onClose={onClose}
      searchResultCount={countData?.count ?? null}
      onSelect={handleSelect}
      onClear={handleClear}
    >
      {isLoading ? (
        <div className="flex justify-center py-8">
          <Loader size="sm" />
        </div>
      ) : (
        <MyNaviFilterJobCategoryAccordion groups={groups} />
      )}
    </MyNaviFilterLayout>
  )
}
